import { FC, useState } from 'react';
import { Upload as UploadIcon } from 'lucide-react';
import {
  teachurKeys,
  queryClient,
  UserProjectStatus,
  useUploadFileMutation,
  useCreateUserProjectMutation,
} from '@teachur/http-client';
import { useSingleCourse } from '@new/features/courses/contexts/SingleCourseProvider';
import { DropZone } from '@new/common/ui/Dropzone/Dropzone';
import { FilePreviewCard } from './FilePreviewCard';
import { getErrorMessage } from '@new/lib';
import { useRequireSubscribed } from '@new/features/subscription/hooks/useRequireSubscribed';
import { toast } from 'sonner';

interface ProjectUploadProps {
  status?: UserProjectStatus;
  selectedProjectId: number | null;
}

export const ProjectUpload: FC<ProjectUploadProps> = ({ status, selectedProjectId }) => {
  const { course } = useSingleCourse();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const requireSubscribed = useRequireSubscribed();

  const uploadFileMutation = useUploadFileMutation({
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to upload file'));
    },
  });

  const createUserProject = useCreateUserProjectMutation({
    onSuccess: () => {
      toast.success('Project submitted successfully!');

      if (!course?.id) return;
      queryClient.invalidateQueries({ queryKey: teachurKeys.courses.completion(course.id) });
      queryClient.invalidateQueries({ queryKey: teachurKeys.courses.detail(course.id) });
      queryClient.invalidateQueries({ queryKey: teachurKeys.userProjects.all });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to submit project'));
    },
  });

  const isLocked = !course?.quizCompleted;

  const hasSubmittedProject =
    status === UserProjectStatus.Pending ||
    status === UserProjectStatus.InReview ||
    status === UserProjectStatus.Approved;

  const canUpload = !isLocked && !hasSubmittedProject;

  const onFileSelect = async (file: File) => {
    setSelectedFile(file);
  };

  const onViewFile = () => {
    if (!selectedFile) return;
    const fileUrl = URL.createObjectURL(selectedFile);
    window.open(fileUrl, '_blank');
  };

  const handleDropZoneError = (error: string) => {
    toast.error(error);
  };

  const onSubmit = requireSubscribed(async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    if (!selectedProjectId) {
      toast.error('Please select a project first');
      return;
    }

    try {
      const uploadedFileData = await uploadFileMutation.mutateAsync(selectedFile);

      await createUserProject.mutateAsync({
        projectId: selectedProjectId,
        file: uploadedFileData,
      });
      setSelectedFile(null);
    } catch (error) {
      console.error('Submission failed:', error);
    }
  });

  if (hasSubmittedProject) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {!selectedFile ? (
        <DropZone
          onUpload={onFileSelect}
          onError={handleDropZoneError}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.webm,.mp3,.wav,.m4a"
          maxSizeMB={200}
          disabled={!canUpload}
        >
          <div className="flex flex-col items-center gap-4 py-6">
            <UploadIcon className="text-(--neutral-black-NB400) w-6 lg:w-8 h-6 lg:h-8" />
            <div className="flex flex-col items-center gap-2 [font-size:var(--fs-l5)]! lg:[font-size:var(--fs-p4)]! text-center">
              <p className=" text-(--neutral-black-NB700)! m-0!">
                Drag and drop your file here, or click to browse
              </p>
              <p className="text-(--neutral-black-NB600)! m-0!">
                Supported formats: PDF, DOC, DOCX, JPG, PNG, GIF, MP4, MOV, AVI, WEBM, MP3, WAV, M4A
                (Max 200MB)
              </p>
            </div>
          </div>
        </DropZone>
      ) : (
        <FilePreviewCard
          fileName={selectedFile.name}
          fileSize={`${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`}
          onView={onViewFile}
          onRemove={() => setSelectedFile(null)}
          onSubmit={onSubmit}
          isSubmitting={uploadFileMutation.isPending || createUserProject.isPending}
        />
      )}
    </div>
  );
};
